import { ReparoApi } from '@api';
import PageHeader from '@components/common/PageHeader';
import AskAiButton from '@components/QuickHelpPanel/AskAiButton';
import { useWidgetContextCapture } from '@hooks/useWidgetContextCapture';
import type { DataSheetFilterConfig } from '@components/ui/DataSheet';
import type { FilterValue } from '@components/ui/EntityFilterDropdown';
import ShareModal from '@components/ui/ShareModal';
import React, { useCallback, useEffect, useMemo, useState } from 'react';

import usePersistentState from '@hooks/usePersistentState';
import { toIsActive, useProductFilterParams } from '@hooks/useProductFilterParams';
import { getMatchingEntityIds } from '@api/entity-attributes';
import { resolveAttributeFilterEntityIds } from '@hooks/useEntityAttributes';
import useToaster from '@hooks/toaster/useToaster';
import { useCurrentUser } from '../../../hooks/useCurrentUser';
import useApp from '@providers/App/useApp';
import { ConsultantCapability } from '../../../constants/consultant-capability';
import {
    buildPreviousRange,
    buildSkuVariancePivot,
    computeForecastAccuracy,
    computeGrowthLabel,
    defaultCurrentPeriod,
    defaultCurrentWeek,
    defaultLast4Weeks,
    normalizeDateKey,
} from './dateUtils';
import DemandChartSection from './demandChartSection';
import DemandTableSection from './demandTableSection';
import type {
    AggregateResponse,
    AggregateRow,
    ChartPoint,
    DateRange,
    Mode,
    PeriodMeta,
    PeriodType,
    SkuPivotRow,
    SkuVarianceApiRow,
    SkuVarianceApiResponse,
} from './types';
import UploadForecastModal from './uploadForecastModal';

type ChannelResponse = {
    channelId?: string;
    name?: string;
    abbreviationKey?: string;
};

type CategoryResponse = string;

type UserResponse = {
    organizationId?: string;
};

type OrganizationResponse = {
    preferredPeriodType?: PeriodType;
};


const DemandContainer: React.FC = () => {
    const { currentUser } = useCurrentUser();
    const { consultantAccess } = useApp();
    const { showToastSuccess } = useToaster();
    const isConsultant = currentUser?.role === 'CONSULTANT';
    const [shareModal, setShareModal] = useState<{ resourceKey: string } | null>(null);

    // Determine which sections consultant has access to
    const hasChartAccess = !isConsultant || consultantAccess.includes(ConsultantCapability.TABLE_DEMAND_CHART);
    const hasTableAccess = !isConsultant || consultantAccess.includes(ConsultantCapability.TABLE_DEMAND_FORECAST);

    const { captureContext: captureChart, addToChat: addChartToChat } = useWidgetContextCapture({
        componentName: 'DemandChartSection',
        label: 'Sales & Run Rate Chart',
    });
    const { captureContext: captureTable, addToChat: addTableToChat } = useWidgetContextCapture({
        componentName: 'DemandTableSection',
        label: 'Sales by SKU and Channel',
    });

    /* ----------------------------- Filter state ----------------------------- */

    // Organization period type (fetched from org config)
    const [preferredPeriodType, setPreferredPeriodType] = useState<PeriodType>('MONTH');

    // Chart filters
    const [chartRange, setChartRange] = usePersistentState<DateRange>(
        'demand_chart_range',
        defaultLast4Weeks(),
    );
    const [chartChannelFilter, setChartChannelFilter] = usePersistentState<FilterValue>(
        'demand_chart_channel_v2',
        {},
    );
    const [chartProductFilter, setChartProductFilter] = usePersistentState<FilterValue>(
        'demand_chart_product_v2',
        {},
    );
    const [mode, setMode] = usePersistentState<Mode>(
        'demand_chart_mode',
        'sales',
    );
    const [showChartCalendar, setShowChartCalendar] = useState(false);

    // Table filters
    const [tableRange, setTableRange] = usePersistentState<DateRange>(
        'demand_table_range',
        defaultCurrentWeek(),
    );
    const [tableChannelFilter, setTableChannelFilter] = usePersistentState<FilterValue>(
        'demand_table_channel_v2',
        {},
    );
    const [tableProductFilter, setTableProductFilter] = usePersistentState<FilterValue>(
        'demand_table_product_v2',
        {},
    );
    const [tableSearch, setTableSearch] = usePersistentState<string>(
        'demand_table_search',
        '',
    );
    const [showTableCalendar, setShowTableCalendar] = useState(false);

    const [channelOptions, setChannelOptions] = useState<{ label: string; value: string }[]>([]);
    const [categoryOptions, setCategoryOptions] = useState<{ label: string; value: string }[]>([]);
    const [productGroupOptions, setProductGroupOptions] = useState<{ label: string; value: string }[]>([]);
    const [tierOptions, setTierOptions] = useState<{ label: string; value: string }[]>([]);

    const filterOptions = useMemo(() => ({ categoryOptions, productGroupOptions, tierOptions }), [categoryOptions, productGroupOptions, tierOptions]);
    const { params: chartProductFilterParams } = useProductFilterParams(filterOptions, chartProductFilter);
    const { params: tableProductFilterParams } = useProductFilterParams(filterOptions, tableProductFilter);

    /* ----------------------------- Data state ------------------------------- */

    const [chartSalesRows, setChartSalesRows] = useState<AggregateRow[]>([]);
    const [chartForecastRows, setChartForecastRows] = useState<AggregateRow[]>(
        [],
    );
    const [prevChartSalesRows, setPrevChartSalesRows] = useState<
        AggregateRow[]
    >([]);

    const [tableSkuVarianceData, setTableSkuVarianceData] = useState<SkuVarianceApiRow[]>([]);

    const [loadingChart, setLoadingChart] = useState(false);
    const [chartError, setChartError] = useState<string | null>(null);

    // upload modal
    const [isUploadModalOpen, setIsUploadModalOpen] = useState(false);

    const [reloadKey, setReloadKey] = useState(0);

    const triggerReload = () => setReloadKey((x) => x + 1);

    /* ---------------------------------------------------------------------- */
    /*                         Fetch: channel options                         */
    /* ---------------------------------------------------------------------- */

    useEffect(() => {
        const fetchChannels = async () => {
            try {
                const channels =
                    ((await ReparoApi({
                        method: 'GET',
                        url: '/channels?limit=200',
                        body: undefined,
                    })) as unknown as ChannelResponse[] | null) ?? [];

                type ChannelOption = {
                    label: string;
                    name: string;
                    value: string;
                };

                // build key using abbreviationKey (preferred), else channelId
                const mapped: ChannelOption[] = channels
                    .map((channel) => {
                        const key =
                            channel.abbreviationKey ||
                            channel.channelId ||
                            channel.name;
                        const value = key;
                        const label =
                            channel.abbreviationKey ||
                            channel.name ||
                            channel.channelId;

                        if (!value || !label) return null;
                        return {
                            label,
                            value: String(value),
                            name:
                                channel.name ||
                                channel.abbreviationKey ||
                                channel.channelId ||
                                String(value),
                        };
                    })
                    .filter(
                        (opt): opt is ChannelOption =>
                            opt !== null && typeof opt.value === 'string',
                    );

                const deduped = Array.from(
                    new Map(mapped.map((opt) => [opt.value, opt])).values(),
                );

                setChannelOptions(deduped.map(({ label, value }) => ({ label, value })));
            } catch {
                setChannelOptions([]);
            }
        };

        void fetchChannels();
    }, []);

    useEffect(() => {
        const fetchCategories = async () => {
            try {
                const categories =
                    ((await ReparoApi({
                        method: 'POST',
                        url: '/products/categories/list',
                        body: null,
                    })) as unknown as CategoryResponse[] | null) ?? [];

                const mapped = categories
                    .map((category) => category?.trim())
                    .filter(Boolean)
                    .map((category) => ({
                        label: category as string,
                        value: category as string,
                    }));

                const deduped = Array.from(
                    new Map(mapped.map((opt) => [opt.value, opt])).values(),
                );

                setCategoryOptions(deduped);
            } catch {
                setCategoryOptions([]);
            }
        };

        void fetchCategories();
    }, []);

    useEffect(() => {
        const fetchProductGroups = async () => {
            try {
                const groups =
                    ((await ReparoApi({
                        method: 'POST',
                        url: '/products/product-groups/list',
                        body: null,
                    })) as unknown as string[] | null) ?? [];

                const mapped = groups
                    .map((g) => g?.trim())
                    .filter(Boolean)
                    .map((g) => ({
                        label: g as string,
                        value: g as string,
                    }));

                const deduped = Array.from(
                    new Map(mapped.map((opt) => [opt.value, opt])).values(),
                );

                setProductGroupOptions(deduped);
            } catch {
                setProductGroupOptions([]);
            }
        };

        void fetchProductGroups();
    }, []);

    const tierChannelKey = useMemo(() => {
        const chartChannels = chartChannelFilter.channel ?? [];
        const tableChannels = tableChannelFilter.channel ?? [];
        if (chartChannels.length === 1) return chartChannels[0];
        if (tableChannels.length === 1) return tableChannels[0];
        return undefined;
    }, [chartChannelFilter.channel, tableChannelFilter.channel]);

    useEffect(() => {
        const fetchTiers = async () => {
            try {
                const tiers =
                    ((await ReparoApi({
                        method: 'POST',
                        url: '/products/tiers/list',
                        body: JSON.stringify({ channel: tierChannelKey }),
                    })) as unknown as string[] | null) ?? [];

                const mapped = tiers
                    .map((t) => t?.trim())
                    .filter(Boolean)
                    .map((t) => ({
                        label: t as string,
                        value: t as string,
                    }));

                const deduped = Array.from(
                    new Map(mapped.map((opt) => [opt.value, opt])).values(),
                );

                setTierOptions(deduped);
            } catch {
                setTierOptions([]);
            }
        };

        void fetchTiers();
    }, [tierChannelKey]);

    /* ---------------------------------------------------------------------- */
    /*                  Fetch: organization preferred period type             */
    /* ---------------------------------------------------------------------- */

    useEffect(() => {
        const fetchPreferredPeriodType = async () => {
            try {
                // Step 1: Get organizationId from /auth/me
                const userData = (await ReparoApi({
                    method: 'GET',
                    url: '/auth/me',
                    body: undefined,
                })) as unknown as UserResponse | null;

                const organizationId = userData?.organizationId;
                if (!organizationId) {
                    console.warn('No organizationId found, using default period type');
                    return;
                }

                // Step 2: Fetch organization details to get preferredPeriodType
                const orgData = (await ReparoApi({
                    method: 'GET',
                    url: `/organizations/${organizationId}`,
                    body: undefined,
                })) as unknown as OrganizationResponse | null;

                if (orgData?.preferredPeriodType) {
                    setPreferredPeriodType(orgData.preferredPeriodType);
                    console.log('Using organization preferred period type:', orgData.preferredPeriodType);
                }
            } catch (error) {
                console.error('Failed to fetch organization period type:', error);
                // Keep default MONTH on error
            }
        };

        void fetchPreferredPeriodType();
    }, []);

    /* ---------------------------------------------------------------------- */
    /*                         Fetch: chart (3 API calls)                     */
    /* ---------------------------------------------------------------------- */

    useEffect(() => {
        const timer = setTimeout(() => {
        const fetchChartData = async () => {
            try {
                setLoadingChart(true);
                setChartError(null);

                // Reset data to avoid stale state
                setChartSalesRows([]);
                setChartForecastRows([]);
                setPrevChartSalesRows([]);

                const channels = chartChannelFilter.channel ?? [];
                const categories = chartProductFilter.category ?? [];
                const productGroups = chartProductFilter.productGroup ?? [];
                const tiers = chartProductFilter.tier ?? [];
                const isActive = toIsActive(chartProductFilter.isActive);
                const attrProductIds = await resolveAttributeFilterEntityIds(chartProductFilter, getMatchingEntityIds);

                // Sales payload uses DAY for granular chart data
                const salesPayload = {
                    startDate: chartRange.startDate,
                    endDate: chartRange.endDate,
                    channels,
                    categories,
                    productGroups,
                    tiers,
                    isActive,
                    ...(attrProductIds !== null && { productIds: attrProductIds }),
                    groupings: ['PRODUCT', 'DATE'],
                    periodType: 'DAY',
                };

                // Forecast payload uses org's preferred period type
                const forecastPayload = {
                    startDate: chartRange.startDate,
                    endDate: chartRange.endDate,
                    channels,
                    categories,
                    productGroups,
                    tiers,
                    isActive,
                    ...(attrProductIds !== null && { productIds: attrProductIds }),
                    groupings: ['PRODUCT', 'DATE'],
                    periodType: preferredPeriodType,
                };

                const prevRange = buildPreviousRange(chartRange);
                const prevSalesPayload = {
                    ...salesPayload,
                    startDate: prevRange.startDate,
                    endDate: prevRange.endDate,
                };

                const results = await Promise.allSettled([
                    ReparoApi({
                        method: 'POST',
                        url: '/sales/aggregate',
                        body: JSON.stringify(salesPayload),
                    }) as unknown as Promise<AggregateResponse>,
                    ReparoApi({
                        method: 'POST',
                        url: '/forecasts/aggregate',
                        body: JSON.stringify(forecastPayload),
                    }) as unknown as Promise<AggregateResponse>,
                    ReparoApi({
                        method: 'POST',
                        url: '/sales/aggregate',
                        body: JSON.stringify(prevSalesPayload),
                    }) as unknown as Promise<AggregateResponse>,
                ]);

                const salesResp =
                    results[0].status === 'fulfilled' ? results[0].value : null;
                const forecastResp =
                    results[1].status === 'fulfilled' ? results[1].value : null;
                const prevSalesResp =
                    results[2].status === 'fulfilled' ? results[2].value : null;

                // Log errors for debugging but allow partial rendering
                if (results[0].status === 'rejected') {
                    console.error('Sales fetch failed', results[0].reason);
                }
                if (results[1].status === 'rejected') {
                    console.error('Forecast fetch failed', results[1].reason);
                }
                if (results[2].status === 'rejected') {
                    console.error(
                        'Previous sales fetch failed',
                        results[2].reason,
                    );
                }

                // Only show user error if the PRIMARY sales data fails
                if (results[0].status === 'rejected') {
                    setChartError('Could not load sales data');
                }

                setChartSalesRows(salesResp?.data ?? []);
                setChartForecastRows(forecastResp?.data ?? []);
                setPrevChartSalesRows(prevSalesResp?.data ?? []);
                captureChart({
                    startDate: chartRange.startDate,
                    endDate: chartRange.endDate,
                    channels: chartChannelFilter.channel ?? [],
                    categories: chartProductFilter.category ?? [],
                    mode,
                });
            } catch (err: any) {
                // This catch block might not be reached with allSettled, but good for safety
                console.error('Unexpected chart fetch error', err);
                setChartError('Failed to load chart data');
            } finally {
                setLoadingChart(false);
            }
        };

        void fetchChartData();
        }, 350);
        return () => clearTimeout(timer);
    }, [chartRange, chartChannelFilter, chartProductFilter, reloadKey, preferredPeriodType]);

    /* ---------------------------------------------------------------------- */
    /*                         Fetch: table (2 API calls)                     */
    /* ---------------------------------------------------------------------- */

    useEffect(() => {
        const timer = setTimeout(() => {
        const fetchTableData = async () => {
            try {
                setTableSkuVarianceData([]);

                const attrProductIds = await resolveAttributeFilterEntityIds(tableProductFilter, getMatchingEntityIds);
                const variancePayload = {
                    startDate: tableRange.startDate,
                    endDate: tableRange.endDate,
                    channels: tableChannelFilter.channel ?? [],
                    categories: tableProductFilter.category ?? [],
                    productGroups: tableProductFilter.productGroup ?? [],
                    tiers: tableProductFilter.tier ?? [],
                    isActive: toIsActive(tableProductFilter.isActive),
                    ...(attrProductIds !== null && { productIds: attrProductIds }),
                    periodType: preferredPeriodType,
                };

                const varianceResp = (await ReparoApi({
                    method: 'POST',
                    url: '/forecasts/sku-variance',
                    body: JSON.stringify(variancePayload),
                })) as unknown as SkuVarianceApiResponse;

                setTableSkuVarianceData(varianceResp?.data ?? []);
                captureTable({
                    startDate: tableRange.startDate,
                    endDate: tableRange.endDate,
                    channels: tableChannelFilter.channel ?? [],
                    categories: tableProductFilter.category ?? [],
                    productGroups: tableProductFilter.productGroup ?? [],
                    periodType: preferredPeriodType,
                });
            } catch (error) {
                console.error('Failed to fetch table data:', error);
                setTableSkuVarianceData([]);
            }
        };

        void fetchTableData();
        }, 350);
        return () => clearTimeout(timer);
    }, [tableRange, tableChannelFilter, tableProductFilter, reloadKey, preferredPeriodType]);

    /* ---------------------------------------------------------------------- */
    /*                             Derived values                              */
    /* ---------------------------------------------------------------------- */

    // Chart time-series
    const chartData = useMemo<ChartPoint[]>(() => {
        const bucket = new Map<string, ChartPoint>();

        const merge = (rows: AggregateRow[], key: 'sales' | 'forecast') => {
            rows.forEach((row) => {
                const dateKey = normalizeDateKey(row.grouping?.DATE?.key);
                if (!dateKey) return;
                const existing =
                    bucket.get(dateKey) ||
                    ({ date: dateKey, sales: 0, forecast: 0 } as ChartPoint);
                existing[key] += row.data.quantity || 0;
                bucket.set(dateKey, existing);
            });
        };

        merge(chartSalesRows, 'sales');
        merge(chartForecastRows, 'forecast');

        return Array.from(bucket.values())
            .filter(
                (p) =>
                    p.date >= chartRange.startDate &&
                    p.date <= chartRange.endDate,
            )
            .sort((a, b) => (a.date || '').localeCompare(b.date || ''));
    }, [chartSalesRows, chartForecastRows, chartRange]);

    console.log('Chart data:', chartData);

    // Chart totals
    const chartTotalSales = useMemo(
        () =>
            chartSalesRows.reduce(
                (sum, row) => sum + (row.data.quantity || 0),
                0,
            ),
        [chartSalesRows],
    );

    const chartPrevTotalSales = useMemo(
        () =>
            prevChartSalesRows.reduce(
                (sum, row) => sum + (row.data.quantity || 0),
                0,
            ),
        [prevChartSalesRows],
    );

    const growthLabel = computeGrowthLabel(
        chartTotalSales,
        chartPrevTotalSales,
    );

    // Table totals derived from the SKU variance response
    const tableTotalSales = useMemo(
        () =>
            tableSkuVarianceData
                .flatMap((r) => r.periods)
                .reduce((sum, p) => sum + p.actual, 0),
        [tableSkuVarianceData],
    );

    const tableTotalForecast = useMemo(
        () =>
            tableSkuVarianceData
                .flatMap((r) => r.periods)
                .reduce((sum, p) => sum + p.forecast, 0),
        [tableSkuVarianceData],
    );

    const forecastAccuracyLabel = computeForecastAccuracy(
        tableTotalSales,
        tableTotalForecast,
    );

    // Period pivot for table built from the SKU variance API response
    const { periods, rows: pivotRows } = useMemo<{
        periods: PeriodMeta[];
        rows: SkuPivotRow[];
    }>(
        () => buildSkuVariancePivot(tableSkuVarianceData),
        [tableSkuVarianceData],
    );

    const filteredRows = useMemo<SkuPivotRow[]>(() => {
        if (!tableSearch.trim()) return pivotRows;
        const q = tableSearch.toLowerCase();
        return pivotRows.filter(
            (row) =>
                row.name.toLowerCase().includes(q) ||
                row.sku.toLowerCase().includes(q),
        );
    }, [pivotRows, tableSearch]);

    /* --------------------------- CSV download --------------------------- */

    const handleDownloadCsv = useCallback(() => {
        if (!periods.length || !filteredRows.length) return;

        const header: string[] = ['SKU', 'Name'];
        periods.forEach((p) => {
            header.push(`${p.label} Forecast`, `${p.label} Actual`, `${p.label} Variance`);
        });

        const lines = filteredRows.map((row) => {
            const cells: (string | number)[] = [row.sku, row.name];
            periods.forEach((p) => {
                const metric = row.metricsByPeriod[p.key] || {};
                const varianceDisplay = metric.variance == null ? '' : `${metric.variance}%`;
                cells.push(metric.forecast ?? '', metric.actual ?? '', varianceDisplay);
            });
            return cells.join(',');
        });

        const csv = [header.join(','), ...lines].join('\n');
        const blob = new Blob([csv], {
            type: 'text/csv;charset=utf-8;',
        });
        const url = URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.href = url;
        link.download = 'forecast_vs_actual.csv';
        link.click();
        URL.revokeObjectURL(url);
    }, [periods, filteredRows]);

    /* ---------------------------------------------------------------------- */
    /*                         Filter configs for DataSheet                    */
    /* ---------------------------------------------------------------------- */

    const chartChannelFilterConfig: DataSheetFilterConfig = useMemo(() => ({
        label: 'Channel',
        params: [{ key: 'channel', label: 'Channel', mode: 'multi', options: channelOptions }],
        value: chartChannelFilter,
        onChange: setChartChannelFilter,
    }), [channelOptions, chartChannelFilter, setChartChannelFilter]);

    const chartProductFilterConfig: DataSheetFilterConfig = useMemo(() => ({
        label: 'Product',
        params: chartProductFilterParams,
        value: chartProductFilter,
        onChange: setChartProductFilter,
    }), [chartProductFilterParams, chartProductFilter, setChartProductFilter]);

    const tableChannelFilterConfig: DataSheetFilterConfig = useMemo(() => ({
        label: 'Channel',
        params: [{ key: 'channel', label: 'Channel', mode: 'multi', options: channelOptions }],
        value: tableChannelFilter,
        onChange: setTableChannelFilter,
    }), [channelOptions, tableChannelFilter, setTableChannelFilter]);

    const tableProductFilterConfig: DataSheetFilterConfig = useMemo(() => ({
        label: 'Product',
        params: tableProductFilterParams,
        value: tableProductFilter,
        onChange: setTableProductFilter,
    }), [tableProductFilterParams, tableProductFilter, setTableProductFilter]);

    /* ---------------------------------------------------------------------- */
    /*                                   UI                                    */
    /* ---------------------------------------------------------------------- */

    return (
        <div className="flex h-full w-full flex-col overflow-y-auto page-shell">
            {/* Shared PageHeader Component */}
            <PageHeader
                icon="📊"
                title="Demand"
                subtitle="Demand tracker and variance analysis"
                actions={<AskAiButton />}
            />

            {/* Chart section - only shown if user has chart access */}
            {hasChartAccess && (
                <DemandChartSection
                    range={chartRange}
                    mode={mode}
                    showCalendar={showChartCalendar}
                    onOpenCalendar={() => setShowChartCalendar(true)}
                    onCloseCalendar={() => setShowChartCalendar(false)}
                    onApplyRange={(r) => {
                        setChartRange(r);
                        setShowChartCalendar(false);
                    }}
                    onChangeMode={setMode}
                    channelFilterConfig={chartChannelFilterConfig}
                    productFilterConfig={chartProductFilterConfig}
                    totalSales={chartTotalSales}
                    growthLabel={growthLabel}
                    data={chartData}
                    loading={loadingChart}
                    error={chartError}
                    onShare={isConsultant ? undefined : () => setShareModal({ resourceKey: ConsultantCapability.TABLE_DEMAND_CHART })}
                    onAddToChat={addChartToChat}
                />
            )}

            {/* Table section - only shown if user has table access */}
            {hasTableAccess && (
                <DemandTableSection
                    periods={periods}
                    rows={filteredRows}
                    preferredPeriodType={preferredPeriodType}
                    tableRange={tableRange}
                    tableSearch={tableSearch}
                    showCalendar={showTableCalendar}
                    onOpenCalendar={() => setShowTableCalendar(true)}
                    onCloseCalendar={() => setShowTableCalendar(false)}
                    onApplyRange={(r) => {
                        setTableRange(r);
                        setShowTableCalendar(false);
                    }}
                    onChangeSearch={setTableSearch}
                    channelFilterConfig={tableChannelFilterConfig}
                    productFilterConfig={tableProductFilterConfig}
                    onDownloadCsv={handleDownloadCsv}
                    onUploadForecast={() => setIsUploadModalOpen(true)}
                    onShare={isConsultant ? undefined : () => setShareModal({ resourceKey: ConsultantCapability.TABLE_DEMAND_FORECAST })}
                    totalSales={tableTotalSales}
                    totalForecast={tableTotalForecast}
                    forecastAccuracyLabel={forecastAccuracyLabel}
                    onAddToChat={addTableToChat}
                />
            )}

            {shareModal && (
                <ShareModal
                    opened={!!shareModal}
                    onClose={() => setShareModal(null)}
                    resourceKey={shareModal.resourceKey}
                    merchantId={currentUser?.organizationId ?? ''}
                />
            )}

            {/* Upload modal */}
            <UploadForecastModal
                open={isUploadModalOpen}
                onClose={() => setIsUploadModalOpen(false)}
                onUploaded={({ processed, channel }) => {
                    setIsUploadModalOpen(false);
                    triggerReload(); // chart + table dono refetch
                    showToastSuccess({
                        title: 'Forecast uploaded',
                        subtitle: `${processed} rows uploaded successfully for ${channel}.`,
                    });
                }}
                channels={[
                    { id: 'Total', label: 'Total' },
                    ...channelOptions
                        .filter((c) => c.value !== 'all')
                        .map((c) => ({ id: String(c.value), label: c.label })),
                ]}
            />
        </div>
    );
};

export default DemandContainer;
